import React, { useState, useEffect } from 'react';
import { Smartphone, Clock, Shield, Timer, Save, Loader2 } from 'lucide-react';
import api from '../services/api';

type MockPolicy = 'STRICT' | 'WARN' | 'OFF';

interface MobileConfig {
  entryStart: string;   // "06:00"
  entryEnd: string;     // "10:00"
  exitStart: string;    // "15:00"
  exitEnd: string;      // "23:59"
  rejectOutsideWindow: boolean;
  mockPolicy: MockPolicy;
  retryLimitSeconds: number;
}

const DEFAULTS: MobileConfig = {
  entryStart: '06:00',
  entryEnd: '10:00',
  exitStart: '15:00',
  exitEnd: '23:59',
  rejectOutsideWindow: false,
  mockPolicy: 'WARN',
  retryLimitSeconds: 60,
};

const MobileSettings: React.FC = () => {
  const [cfg, setCfg] = useState<MobileConfig>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const r: any = await api.get('/settings/Mobile');
      const dict = (r?.data ?? r ?? {}) as Record<string, any>;
      setCfg({
        entryStart: dict.EntryStart ?? DEFAULTS.entryStart,
        entryEnd:   dict.EntryEnd   ?? DEFAULTS.entryEnd,
        exitStart:  dict.ExitStart  ?? DEFAULTS.exitStart,
        exitEnd:    dict.ExitEnd    ?? DEFAULTS.exitEnd,
        rejectOutsideWindow: String(dict.RejectOutsideWindow ?? 'false').toLowerCase() === 'true',
        mockPolicy: ((dict.MockPolicy ?? 'WARN') as string).toUpperCase() as MockPolicy,
        retryLimitSeconds: parseInt(String(dict.RetryLimitSeconds ?? '60'), 10) || 60,
      });
    } catch {
      // sessiz — henuz kayit yoksa default kullan
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const flash = (type: 'success' | 'error', text: string) => {
    setMsg({ type, text }); setTimeout(() => setMsg(null), 4000);
  };

  const validTime = (t: string) => /^\d{2}:\d{2}$/.test(t);

  const save = async () => {
    if (!validTime(cfg.entryStart) || !validTime(cfg.entryEnd) || !validTime(cfg.exitStart) || !validTime(cfg.exitEnd)) {
      flash('error', 'Saat formatı HH:mm olmalı');
      return;
    }
    setSaving(true);
    const entries: [string, string, string][] = [
      ['EntryStart', cfg.entryStart, 'Mesai giris baslangic saati'],
      ['EntryEnd',   cfg.entryEnd,   'Mesai giris bitis saati'],
      ['ExitStart',  cfg.exitStart,  'Mesai cikis baslangic saati'],
      ['ExitEnd',    cfg.exitEnd,    'Mesai cikis bitis saati'],
      ['RejectOutsideWindow', String(cfg.rejectOutsideWindow), 'Saat penceresi disi girisleri reddet'],
      ['MockPolicy', cfg.mockPolicy, 'Sahte konum politikasi (STRICT/WARN/OFF)'],
      ['RetryLimitSeconds', String(cfg.retryLimitSeconds), 'Tekrar deneme minimum sure (saniye)'],
    ];
    let ok = 0, err = 0;
    for (const [key, value, desc] of entries) {
      try {
        await api.put(`/settings/Mobile/${key}`, { value, description: desc });
        ok++;
      } catch { err++; }
    }
    setSaving(false);
    flash(err > 0 ? 'error' : 'success', err > 0 ? `${ok} kaydedildi, ${err} hata` : 'Mobil ayarlar kaydedildi');
    if (err === 0) await load();
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 size={32} className="animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-2xl bg-amber-100 text-amber-600 flex items-center justify-center flex-shrink-0">
          <Smartphone size={24} />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-800">Mobil (NexVera) Ayarları</h1>
          <p className="text-sm text-slate-500 mt-1">
            Giriş/çıkış saat penceresi, sahte konum politikası ve tekrar deneme sınırı
          </p>
        </div>
      </div>

      {msg && (
        <div className={`p-3 rounded-xl text-sm font-bold ${
          msg.type === 'success' ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                                 : 'bg-red-50 border border-red-200 text-red-700'
        }`}>{msg.text}</div>
      )}

      {/* KART 1: Mesai Saat Penceresi */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={20} className="text-blue-600" />
          <h2 className="text-lg font-black text-slate-800">Mesai Saat Penceresi</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TimeField label="Giriş Başlangıcı" value={cfg.entryStart} onChange={v => setCfg({ ...cfg, entryStart: v })} />
          <TimeField label="Giriş Bitişi"     value={cfg.entryEnd}   onChange={v => setCfg({ ...cfg, entryEnd: v })} />
          <TimeField label="Çıkış Başlangıcı" value={cfg.exitStart}  onChange={v => setCfg({ ...cfg, exitStart: v })} />
          <TimeField label="Çıkış Bitişi"     value={cfg.exitEnd}    onChange={v => setCfg({ ...cfg, exitEnd: v })} />
        </div>

        <label className="mt-4 flex items-start gap-3 cursor-pointer p-3 border border-slate-200 rounded-xl hover:bg-slate-50">
          <input
            type="checkbox"
            checked={cfg.rejectOutsideWindow}
            onChange={e => setCfg({ ...cfg, rejectOutsideWindow: e.target.checked })}
            className="mt-1 w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
          />
          <div>
            <p className="text-sm font-bold text-slate-800">Saat penceresi dışında girişleri reddet</p>
            <p className="text-xs text-slate-500 mt-0.5">
              <b>Kapalı</b>: pencere dışı girişler kayıt olur ve "mesai dışı" olarak işaretlenir. <b>Açık</b>: pencere dışı girişler tamamen reddedilir.
            </p>
          </div>
        </label>
      </section>

      {/* KART 2: Sahte Konum Politikasi */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Shield size={20} className="text-emerald-600" />
          <h2 className="text-lg font-black text-slate-800">Sahte Konum (Mock Location) Politikası</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <PolicyCard
            title="SIKI"
            desc="Sahte konum tespit edilirse giriş reddedilir"
            active={cfg.mockPolicy === 'STRICT'}
            color="red"
            onClick={() => setCfg({ ...cfg, mockPolicy: 'STRICT' })}
          />
          <PolicyCard
            title="UYARI"
            desc="Sahte konum kayıt olur ama 'şüpheli' işaretiyle"
            active={cfg.mockPolicy === 'WARN'}
            color="amber"
            onClick={() => setCfg({ ...cfg, mockPolicy: 'WARN' })}
          />
          <PolicyCard
            title="KAPALI"
            desc="Sahte konum kontrolü yapılmaz"
            active={cfg.mockPolicy === 'OFF'}
            color="slate"
            onClick={() => setCfg({ ...cfg, mockPolicy: 'OFF' })}
          />
        </div>
      </section>

      {/* KART 3: Tekrar Deneme Sınırı */}
      <section className="bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center gap-2 mb-4">
          <Timer size={20} className="text-purple-600" />
          <h2 className="text-lg font-black text-slate-800">Tekrar Deneme Sınırı</h2>
        </div>

        <div className="flex items-center gap-3">
          <input
            type="number"
            min={0}
            max={3600}
            value={cfg.retryLimitSeconds}
            onChange={e => setCfg({ ...cfg, retryLimitSeconds: Math.max(0, parseInt(e.target.value, 10) || 0) })}
            className="w-32 px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none font-mono text-lg text-center"
          />
          <span className="text-slate-600">saniye</span>
        </div>
        <p className="text-xs text-slate-500 mt-2">
          Bir personelin tekrar giriş deneyebilmesi için bu süre kadar beklemesi gerekir. Dakikada bir okutma yapan "test" kullanıcılarını engeller.
        </p>
      </section>

      {/* Kaydet */}
      <div className="flex justify-end gap-3 pb-4">
        <button
          onClick={load}
          className="px-5 py-2.5 border border-slate-200 rounded-xl text-sm font-bold text-slate-700 hover:bg-slate-50"
        >
          İptal
        </button>
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-bold shadow-lg shadow-orange-500/30 hover:from-amber-600 hover:to-orange-600 disabled:opacity-60"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Kaydet
        </button>
      </div>
    </div>
  );
};

const TimeField: React.FC<{ label: string; value: string; onChange: (v: string) => void }> = ({ label, value, onChange }) => (
  <div>
    <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">{label}</label>
    <input
      type="text"
      inputMode="numeric"
      maxLength={5}
      placeholder="HH:mm"
      value={value}
      onChange={e => {
        let v = e.target.value.replace(/[^0-9:]/g, '').substring(0, 5);
        if (v.length === 2 && !v.includes(':') && value.length !== 3) v += ':';
        onChange(v);
      }}
      className="w-full px-4 py-2.5 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none font-mono text-lg tracking-wider"
    />
  </div>
);

const PolicyCard: React.FC<{ title: string; desc: string; active: boolean; color: 'red' | 'amber' | 'slate'; onClick: () => void }> = ({ title, desc, active, color, onClick }) => {
  const activeCls: Record<string, string> = {
    red:   'border-red-400 bg-red-50 ring-2 ring-red-200',
    amber: 'border-amber-400 bg-amber-50 ring-2 ring-amber-200',
    slate: 'border-slate-400 bg-slate-100 ring-2 ring-slate-200',
  };
  const titleCls: Record<string, string> = {
    red: 'text-red-700', amber: 'text-amber-700', slate: 'text-slate-700',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left p-4 border rounded-xl transition-all ${
        active ? activeCls[color] : 'border-slate-200 bg-white hover:bg-slate-50'
      }`}
    >
      <p className={`text-sm font-black ${titleCls[color]}`}>{title}</p>
      <p className="text-xs text-slate-600 mt-1 leading-snug">{desc}</p>
    </button>
  );
};

export default MobileSettings;
